import dayjs from 'dayjs';
import { bool, date, number, object } from 'yup';
import type { ValidationErrors } from 'final-form';

import { createValidator } from '@src/validation';
import { innKppValidator } from '@src/validation/innKppValidator';
import { textValidator } from '@src/validation/textValidator';
import { counterpartiesStore } from '@stores/dictionaryStores/counterpartiesStore/counterpartiesStore';
import { counterpartyEditStore } from '@stores/dictionaryStores/counterpartiesStore/counterpartyEditStore';
import type { CompanyDto } from '@src/api';

const validateSchema = (values: CompanyDto & { counterparty?: boolean }) =>
  object().shape({
    counterparty: bool().nullable().required(),
    resident: bool().nullable().required(),
    companyCounterparty: bool().nullable().required(),
    code: textValidator({ fieldName: 'code' }).test(
      'code',
      'Запись с данным кодом, типом контрагента и организацией уже существует',
      (value, { parent: { counterpartyTypeCode, orgLocal = null } }) => {
        const { counterparties } = counterpartiesStore;
        const { isNew } = counterpartyEditStore;

        if (!isNew) return true;

        const coincidenceInnCompany = counterparties.find(
          (item) =>
            item.code === value && item.counterpartyTypeCode == counterpartyTypeCode && item.orgLocal === orgLocal,
        );

        return value ? !coincidenceInnCompany : true;
      },
    ),
    inn: innKppValidator({
      fieldName: 'inn',
      required: values.resident,
      innLength: values.counterparty ? 10 : 12,
    }).test('inn', 'Данные ИНН и КПП уже используются', (value, { parent: { code, kpp } }) => {
      const { counterparties } = counterpartiesStore;

      const coincidenceInnCompany = counterparties.find((item) => {
        if (item.kpp || kpp) {
          return item.inn === value && item.kpp === kpp && item.code !== code;
        } else {
          return item.inn === value && item.code !== code;
        }
      });

      return value ? !coincidenceInnCompany : true;
    }),
    taxpayerCode: textValidator({ fieldName: 'taxpayerCode', required: false }),
    registrationNumber: textValidator({
      fieldName: 'registrationNumber',
      required: !values.resident,
      lengthMessage: 'Поле должно содержать 13 символов',
      length: values.resident ? 13 : undefined,
    }),
    kpp: innKppValidator({
      fieldName: 'kpp',
      required: values.resident && values.counterparty,
    }),
    thesOksmCode: number()
      .nullable()
      .test('thesOksmCode', 'Поле должно быть заполнено', (value, { parent: { resident } }) => !(!resident && !value)),
    ogrnip: textValidator({
      fieldName: 'ogrnip',
      required: false,
      lengthMessage: 'Поле должно содержать 15 символов',
      length: !values.counterparty ? 15 : undefined,
    }),
    name: textValidator({ fieldName: 'name' }),
    fullName: textValidator({ fieldName: 'fullName' }),
    latinName: textValidator({ fieldName: 'latinName', required: false }),
    legalAddress: textValidator({ fieldName: 'legalAddress', required: false }),
    counterpartyLastName: textValidator({ fieldName: 'counterpartyLastName', required: !values.counterparty }),
    counterpartyFirstName: textValidator({ fieldName: 'counterpartyFirstName', required: !values.counterparty }),
    counterpartyMiddleName: textValidator({ fieldName: 'counterpartyMiddleName', required: false }),
    startDate: date().nullable().required(),
    endDate: date()
      .nullable()
      .test('isBefore', 'Дата окончания должна быть позже даты начала', (value, { parent: { startDate } }) => {
        if (!startDate) return true;

        return !dayjs(new Date(`${value}`)).isBefore(dayjs(new Date(startDate)));
      }),
  });

export const validate = (
  val: CompanyDto & { counterparty?: boolean }[],
): ((values: unknown) => ValidationErrors | Promise<ValidationErrors> | undefined) =>
  createValidator(validateSchema(val));
